import {Injectable}                  from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {tap}                         from 'rxjs/operators';
import {StorageService}              from '@src/app/core/services/storage.service';
import {ApiService}                  from '@src/app/shared/services/api.service';
import {Services} from '@src/app/core/enums/services.enum';

@Injectable({
    providedIn: 'root'
})
export class SettingsService
{
    public pricingEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public enabledServices$ = new BehaviorSubject<string[]>([Services.FireStopping, Services.FireDoors]);
    public setupCompleted = true;

    public constructor(
        private apiService: ApiService,
        private storageService: StorageService,
    )
    {
        this.bootstrapFromStorage();
    }

    public bootstrap(settings: Array<{ feature?: string, name?: string, payload?: unknown, value?: unknown }> = []): void
    {
        this.hydrateServices(settings);
        const pricingSetting = settings.find(setting => setting?.feature === 'pricing' && setting?.name === 'pricing_enabled');

        if (!pricingSetting) {
            this.bootstrapFromStorage();
            return;
        }

        this.setPricingEnabled(this.resolveBoolean(pricingSetting.payload ?? pricingSetting.value, true));
    }

    public refresh(): Observable<any>
    {
        return this.apiService.list('settings').pipe(
            tap(response => this.hydrate(response?.data ?? []))
        );
    }

    public refreshPreferences(): Observable<any>
    {
        return this.apiService.get('settings/preferences').pipe(tap(response => this.hydrate(response?.data ?? [])));
    }

    public isServiceEnabled(id: string | number | null | undefined): boolean
    {
        return ![Services.FireStopping, Services.FireDoors].includes(String(id) as Services) || this.enabledServices$.value.includes(String(id));
    }

    private hydrateServices(settings: Array<{feature?: string, name?: string, payload?: unknown, value?: unknown}>): void
    {
        const rows = settings.filter(setting => setting.feature === 'services');
        const selected = rows.find(setting => setting.name === 'enabled_services');
        let value = selected?.payload ?? selected?.value;
        if (typeof value === 'string') {
            try { value = JSON.parse(value); } catch { value = null; }
        }
        const enabled = Array.isArray(value) ? value.map(String).filter(id => id === Services.FireStopping || id === Services.FireDoors) : [];
        this.enabledServices$.next(enabled.length ? enabled : [Services.FireStopping, Services.FireDoors]);
        const completed = rows.find(setting => setting.name === 'setup_completed');
        this.setupCompleted = this.resolveBoolean(completed?.payload ?? completed?.value, true);
        for (const row of rows) { this.syncTenantSetting('services', row.name, row.payload ?? row.value); }
    }

    public hydrate(settings: Array<{ feature?: string, name?: string, payload?: unknown, value?: unknown }> = []): void
    {
        this.hydrateServices(settings);
        const pricingSetting = settings.find(setting => setting?.feature === 'pricing' && setting?.name === 'pricing_enabled');
        this.setPricingEnabled(this.resolveBoolean(pricingSetting?.value ?? pricingSetting?.payload, true));
    }

    public setPricingEnabled(enabled: boolean): void
    {
        this.pricingEnabled$.next(enabled);
        this.storageService.write('pricing_enabled', JSON.stringify(enabled));
        this.syncTenantSetting('pricing', 'pricing_enabled', enabled);
    }

    public isPricingEnabled(): boolean
    {
        return this.pricingEnabled$.value;
    }

    public clear(): void
    {
        this.enabledServices$.next([Services.FireStopping, Services.FireDoors]);
        this.setupCompleted = true;
        this.pricingEnabled$.next(true);
        this.storageService.remove('pricing_enabled');
    }

    private bootstrapFromStorage(): void
    {
        try { this.hydrateServices(JSON.parse(this.storageService.read('tenant') || 'null')?.settings ?? []); } catch { this.hydrateServices([]); }
        this.pricingEnabled$.next(this.resolveBoolean(this.storageService.read('pricing_enabled'), true));
    }

    private resolveBoolean(value: unknown, fallback: boolean): boolean
    {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue.length) {
                return fallback;
            }

            if (trimmedValue === 'true' || trimmedValue === '1') {
                return true;
            }

            if (trimmedValue === 'false' || trimmedValue === '0') {
                return false;
            }

            try {
                return this.resolveBoolean(JSON.parse(trimmedValue), fallback);
            } catch {
                return fallback;
            }
        }

        if (typeof value === 'number') {
            return value === 1;
        }

        return fallback;
    }

    private syncTenantSetting(feature: string, name: string, payload: unknown): void
    {
        const rawTenant = this.storageService.read('tenant');

        if (!rawTenant) {
            return;
        }

        try {
            const tenant = JSON.parse(rawTenant);
            const settings = Array.isArray(tenant?.settings) ? tenant.settings : [];
            const settingIndex = settings.findIndex(setting => setting?.feature === feature && setting?.name === name);
            const setting = {
                feature,
                name,
                payload,
            };

            if (settingIndex === -1) {
                settings.push(setting);
            } else {
                settings[settingIndex] = {...settings[settingIndex], ...setting};
            }

            tenant.settings = settings;
            this.storageService.write('tenant', JSON.stringify(tenant));
        } catch {
            // Ignore invalid cached tenant data and continue using the dedicated setting cache.
        }
    }
}
